import { useEffect, useRef, useState } from 'react';

const SCROLL_THRESHOLD = 5;

const getScrollY = () => {
  if (typeof window === 'undefined') {
    return 0;
  }

  return (
    window.scrollY ||
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0
  );
};

/**
 * Tracks scroll direction and returns whether the topbar should be visible.
 * Visible at the top of the page and when scrolling up; hidden when scrolling down.
 *
 * @param {boolean} enabled - Whether scroll tracking is active
 * @returns {boolean} Whether the topbar should be visible
 */
const useScrollDirection = (enabled = true) => {
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }

    lastScrollY.current = getScrollY();

    // Resolve the scroll offset for the element that actually fired the scroll
    // event. Some pages (e.g. the map search view) scroll an inner container
    // instead of the window, in which case window.scrollY stays 0.
    const getEventScrollY = e => {
      const target = e?.target;
      const isScrollableElement =
        target &&
        target.nodeType === 1 &&
        target !== document.documentElement &&
        target !== document.body;
      return isScrollableElement ? target.scrollTop : getScrollY();
    };

    const handleScroll = e => {
      const currentScrollY = getEventScrollY(e);

      if (currentScrollY <= 0) {
        setIsVisible(true);
      } else if (Math.abs(currentScrollY - lastScrollY.current) >= SCROLL_THRESHOLD) {
        setIsVisible(currentScrollY < lastScrollY.current);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });

    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      document.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, [enabled]);

  return isVisible;
};

export default useScrollDirection;
